/**
 * The `onRecoverableError` half of `hydrateRoot` — a RECOVERED hydration
 * mismatch is reported as what it is, by the component that produced it.
 *
 * # The hole this closes (LET-122)
 *
 * React 19 hands a recoverable error — a hydration mismatch (#418), an error
 * during hydration it recovered from by client-rendering the root (#423), a
 * Suspense boundary that switched to client rendering — to the root's
 * `onRecoverableError`. When the root does not declare one, React's default is
 * `reportError(error)`, i.e. a synthetic `window` `error` event, and this app's
 * window handler then logged it as `[browser] Uncaught error` at level 50
 * with `component: 'window'`. Measured in prod (Loki, `{app="web",
 * cluster="prod"}`, rolling 3 days ending 2026-09-20): **12 occurrences**,
 * every one of them anonymous — the message is `Minified React error #418;
 * visit https://react.dev/errors/418?args[]=text&args[]= …`, the stack is
 * `at Za (entry-client-….js:8:31372)`, and the ONE field that names the
 * producer — React's `componentStack`, which only `onRecoverableError`
 * receives — was discarded before it reached the reporter. So the log said a
 * mismatch happened, on a page, and could not say which text.
 *
 * Two things were wrong with that line and this module fixes both:
 *
 * 1. **Severity.** React RECOVERED — the subtree was client-rendered and the
 *    user saw a working page. An "uncaught error" is the wrong sentence for a
 *    condition React handled; it is a hydration mismatch, a `warn`, and it is
 *    counted on `web_browser_errors{component="hydration"}` (the ingest counts
 *    both levels, so demoting it changes the label, not the count or the
 *    alert — `server/api/telemetry.ts` carries that argument).
 * 2. **Attribution.** The component stack React hands over is parsed into its
 *    component frames and the innermost one goes into the MESSAGE (`at
 *    <EstateCard>`), so the client logger's 2 s `level:msg` de-duplication
 *    collapses one producer's repeats while keeping two producers on one page
 *    distinct — and so a Loki reader can group by producer without opening
 *    the context. The whole frame list rides in the context as an ARRAY (the
 *    ingest truncates STRING values at 500 bytes, which would have cut a
 *    deep stack's outer frames off).
 *
 * Because `onRecoverableError` is declared, React no longer calls
 * `reportError` for these, so the `window` handler in `entry-client.tsx`
 * stops double-reporting them by construction — nothing there had to learn
 * to filter React's messages out.
 *
 * ⚠ In a minified bundle a component's `name` IS its minified identifier
 * unless the bundler keeps names — `esbuild.client.mjs` sets `keepNames` for
 * exactly this reason; without it the frame list here would read `Za`, `Ss`
 * and the report would be as anonymous as the one it replaces.
 *
 * # What this does NOT do
 *
 * It does not fix any producer. Half 2 of LET-122 — a number, a date or a
 * locale string formatted with a different resolved locale or timezone on the
 * two sides — starts from the component names THIS module puts in the log.
 * And the hermetic `//web:e2e` sweep's zero-hydration-error assertion
 * (`web/e2e/route-invariants/hydration.ts`) still bites: the `warn` this
 * emits also goes to `console.warn` and its text says "Hydration", which is
 * exactly the pattern that assertion reads.
 */
import { clientLogger } from '@/shared/lib/client-logger';
import type { RootOptions } from 'react-dom/client';

/** The second argument React passes to `onRecoverableError`. */
export type RecoverableErrorInfo = Parameters<NonNullable<RootOptions['onRecoverableError']>>[1];

/**
 * What kind of recoverable error React reported — a closed set, so a reader
 * (or a Loki query) can select the hydration classes without matching on
 * React's message text.
 */
export type RecoverableErrorKind =
  /** React 19 #418 (server HTML / text / attribute differs from the client), React 18 #425 (text). */
  | 'hydration-mismatch'
  /** #423 — an error while hydrating; React recovered by client-rendering the entire root. */
  | 'hydration-root-fallback'
  /** #419 / #421 / #422 / #424 — one Suspense boundary switched to client rendering. */
  | 'suspense-client-render'
  /** A recoverable error this table does not know. Reported at `error` so someone looks. */
  | 'unknown';

export interface ClassifiedRecoverableError {
  kind: RecoverableErrorKind;
  /** React's numbered error (`#418`) when the message carries one — production builds always do. */
  code: number | undefined;
  /**
   * The `args[]=` values of a minified message — for #418 the first is what
   * mismatched (`text` / `HTML` / an attribute name). Empty for a dev-build
   * message, whose text carries the same information in prose.
   */
  args: string[];
  message: string;
}

/**
 * React's numbered errors → kind. The numbers are React's own error-code
 * registry (`scripts/error-codes/codes.json`); the route-invariants sweep's
 * `HYDRATION_FAILURE_PATTERNS` names the same three hydration codes.
 */
const KIND_BY_CODE: ReadonlyMap<number, RecoverableErrorKind> = new Map<
  number,
  RecoverableErrorKind
>([
  [418, 'hydration-mismatch'],
  [425, 'hydration-mismatch'],
  [423, 'hydration-root-fallback'],
  [419, 'suspense-client-render'],
  [421, 'suspense-client-render'],
  [422, 'suspense-client-render'],
  [424, 'suspense-client-render'],
]);

/**
 * A development build says the sentence instead of the number. Ordered: the
 * root-fallback and Suspense sentences also contain "hydrat", so they are
 * tested before the generic mismatch patterns.
 */
const KIND_BY_DEV_TEXT: ReadonlyArray<readonly [RegExp, RecoverableErrorKind]> = [
  [/while hydrating but React was able to recover/i, 'hydration-root-fallback'],
  [/Switched to client rendering/i, 'suspense-client-render'],
  [/switch to client rendering/i, 'suspense-client-render'],
  [/Hydration failed because/i, 'hydration-mismatch'],
  [/didn't match the client/i, 'hydration-mismatch'],
  [/Text content does not match/i, 'hydration-mismatch'],
];

const MINIFIED_CODE = /Minified React error #(\d+)/;
const MINIFIED_ARG = /[?&]args\[\]=([^&\s]*)/g;

/** How many component frames ride in the report's context. */
const MAX_FRAMES = 24;

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error !== null && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message: unknown };
    if (typeof message === 'string') return message;
  }
  return String(error);
}

/** Classify what React reported, from its message alone. Pure. */
export function classifyRecoverableError(error: unknown): ClassifiedRecoverableError {
  const message = messageOf(error);
  const codeMatch = MINIFIED_CODE.exec(message);
  const code = codeMatch ? Number(codeMatch[1]) : undefined;
  const args = [...message.matchAll(MINIFIED_ARG)].map((m) => decodeURIComponent(m[1] ?? ''));

  let kind: RecoverableErrorKind | undefined =
    code === undefined ? undefined : KIND_BY_CODE.get(code);
  if (kind === undefined) {
    kind = KIND_BY_DEV_TEXT.find(([pattern]) => pattern.test(message))?.[1];
  }
  return { kind: kind ?? 'unknown', code, args, message };
}

/**
 * The component names in a React component stack, innermost first, host
 * elements (`div`, `span`) excluded. React's format is one `\n    at Name
 * (location)` line per frame; a production build carries no location.
 */
export function componentFrames(componentStack: string | null | undefined): string[] {
  if (!componentStack) return [];
  const frames: string[] = [];
  for (const line of componentStack.split('\n')) {
    const m = /^\s*at\s+([^\s(]+)/.exec(line);
    if (!m) continue;
    const name = m[1] ?? '';
    // A host element is lower-case and names no producer; a component starts
    // with an upper-case letter, `_` or `$` (esbuild's kept names included).
    if (!/^[A-Z_$]/.test(name)) continue;
    frames.push(name);
    if (frames.length === MAX_FRAMES) break;
  }
  return frames;
}

function sentence(classified: ClassifiedRecoverableError, at: string): string {
  const code = classified.code === undefined ? '' : ` (#${classified.code})`;
  const what = classified.args[0] ? ` ${classified.args[0]}` : '';
  switch (classified.kind) {
    case 'hydration-mismatch':
      return `Hydration mismatch${code}${what} recovered by client render at <${at}>`;
    case 'hydration-root-fallback':
      return `Hydration error${code} recovered by client-rendering the root at <${at}>`;
    case 'suspense-client-render':
      return `Suspense boundary switched to client rendering after a hydration error${code} at <${at}>`;
    case 'unknown':
      return `Recoverable React error${code} at <${at}>`;
  }
}

/**
 * The `onRecoverableError` for `hydrateRoot` — pass it as the root option, see
 * `entry-client.tsx`. Known hydration classes report at `warn` (React
 * recovered; the page works); an unknown recoverable error reports at `error`
 * so it is looked at and, once understood, given a row above.
 */
export function reportRecoverableError(error: unknown, errorInfo: RecoverableErrorInfo): void {
  const classified = classifyRecoverableError(error);
  const frames = componentFrames(errorInfo.componentStack);
  const at = frames[0] ?? 'unknown';
  const context = {
    component: 'hydration' as const,
    kind: classified.kind,
    code: classified.code,
    args: classified.args,
    at,
    componentStack: frames,
    error,
  };
  const message = sentence(classified, at);
  if (classified.kind === 'unknown') clientLogger.error(context, message);
  else clientLogger.warn(context, message);
}
